import { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    AccessTime as PendingIcon,
    AssignmentTurnedIn as RequestsIcon,
    Cancel as DeniedIcon,
    Check as AcceptIcon,
    CheckCircle as AcceptedIcon,
    Close as DenyIcon,
} from '@mui/icons-material';
import { close } from '@/store/uiSlice';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
    useGetAdminCategoryRequestsQuery,
    useGetResponsibleCategoryRequestsQuery,
    useGetUserInfoQuery,
    useUpdateAdminCategoryRequestMutation,
} from '@/store/apiSlice';
import { useI18nContext } from '@/i18n/i18n-react';
import CategoryIcon from '@/components/CategoryIcon';
import type { CategoryRequest } from '@/types';

type FilterStatus = CategoryRequest['status'] | 'all';

const CategoryRequestsReviewModal = () => {
    const { LL } = useI18nContext();
    const dispatch = useAppDispatch();

    const open = useAppSelector((state) => state.ui.open.categoryRequestsReview);
    const onClose = () => dispatch(close('categoryRequestsReview'));

    const { data: userInfo } = useGetUserInfoQuery();

    const [filterStatus, setFilterStatus] = useState<FilterStatus>('pending');

    const isAdmin = !!userInfo?.admin;
    const isResponsible = (userInfo?.categoriesResponsible?.length ?? 0) > 0;
    const canReview = isAdmin || isResponsible;

    const queryArgs = { status: filterStatus } as const;
    const adminQuery = useGetAdminCategoryRequestsQuery(queryArgs, { skip: !open || !isAdmin });
    const responsibleQuery = useGetResponsibleCategoryRequestsQuery(queryArgs, {
        skip: !open || isAdmin || !isResponsible,
    });

    const requestsData = isAdmin ? adminQuery.data : responsibleQuery.data;
    const isLoading = isAdmin ? adminQuery.isLoading : responsibleQuery.isLoading;
    const refetch = isAdmin ? adminQuery.refetch : responsibleQuery.refetch;

    const requests = requestsData?.data || [];

    const pendingCount = useMemo(
        () => requests.filter((req) => req.status === 'pending').length,
        [requests]
    );

    const [updateRequest, { isLoading: isUpdating }] = useUpdateAdminCategoryRequestMutation();

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState<CategoryRequest | null>(null);
    const [confirmAction, setConfirmAction] = useState<'accept' | 'deny'>('accept');
    const [adminNote, setAdminNote] = useState('');

    useEffect(() => {
        if (open && canReview) {
            refetch();
        }
        if (!open) {
            // reset lightweight state on close
            setFilterStatus('pending');
            setConfirmOpen(false);
            setSelectedRequest(null);
            setAdminNote('');
            setConfirmAction('accept');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, canReview]);

    const getStatusIcon = (status: CategoryRequest['status']) => {
        switch (status) {
            case 'pending':
                return <PendingIcon color="warning" />;
            case 'accepted':
                return <AcceptedIcon color="success" />;
            case 'denied':
                return <DeniedIcon color="error" />;
        }
    };

    const getRequestTypeColor = (type: string): 'success' | 'warning' => {
        return type === 'join' ? 'success' : 'warning';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const handleOpenConfirm = (request: CategoryRequest, action: 'accept' | 'deny') => {
        setSelectedRequest(request);
        setConfirmAction(action);
        setAdminNote('');
        setConfirmOpen(true);
    };

    const handleCloseConfirm = () => {
        setConfirmOpen(false);
        setSelectedRequest(null);
        setAdminNote('');
    };

    const handleConfirm = async () => {
        if (!selectedRequest) return;
        await updateRequest({ id: selectedRequest.id, action: confirmAction, adminNote }).unwrap();
        handleCloseConfirm();
        refetch();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RequestsIcon />
                {LL.categoryRequests.title()}
                {pendingCount > 0 && (
                    <Chip
                        label={LL.categoryRequests.pendingCount({ count: pendingCount })}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ ml: 1 }}
                    />
                )}
            </DialogTitle>

            <DialogContent dividers>
                {!canReview ? (
                    <Alert severity="info">{LL.unauthorized.title()}</Alert>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Tabs
                            value={filterStatus}
                            onChange={(_, value) => setFilterStatus(value)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}
                        >
                            <Tab label={LL.categoryRequests.filterAll()} value="all" />
                            <Tab
                                label={LL.categoryRequests.filterPending()}
                                value="pending"
                                icon={
                                    pendingCount > 0 ? (
                                        <Chip
                                            label={pendingCount}
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                        />
                                    ) : undefined
                                }
                                iconPosition="end"
                            />
                            <Tab label={LL.categoryRequests.filterAccepted()} value="accepted" />
                            <Tab label={LL.categoryRequests.filterDenied()} value="denied" />
                        </Tabs>

                        {isLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : requests.length === 0 ? (
                            <Alert severity="info">
                                {filterStatus === 'pending'
                                    ? LL.categoryRequests.noPendingRequests()
                                    : LL.categoryRequests.noRequestsDescription()}
                            </Alert>
                        ) : (
                            <Stack spacing={2}>
                                {requests.map((request) => (
                                    <Card key={request.id} variant="outlined">
                                        <CardContent>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    gap: 2,
                                                }}
                                            >
                                                <Box sx={{ flex: 1 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: {
                                                                xs: 'column',
                                                                sm: 'row',
                                                            },
                                                            alignItems: {
                                                                xs: 'flex-start',
                                                                sm: 'center',
                                                            },
                                                            gap: 2,
                                                            mb: 2,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                            }}
                                                        >
                                                            <CategoryIcon
                                                                category={request.categoryIcon}
                                                            />
                                                            <Box>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                >
                                                                    {request.categoryName}
                                                                </Typography>
                                                                <Typography variant="body1">
                                                                    {request.resourceName}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                gap: 1,
                                                                flexWrap: 'wrap',
                                                            }}
                                                        >
                                                            <Chip
                                                                label={
                                                                    request.requestType === 'join'
                                                                        ? LL.categoryRequests.join()
                                                                        : LL.categoryRequests.leave()
                                                                }
                                                                size="small"
                                                                variant="outlined"
                                                                color={getRequestTypeColor(
                                                                    request.requestType
                                                                )}
                                                            />
                                                            <Chip
                                                                icon={getStatusIcon(request.status)}
                                                                label={
                                                                    request.status === 'pending'
                                                                        ? LL.categoryRequests.pending()
                                                                        : request.status ===
                                                                            'accepted'
                                                                          ? LL.categoryRequests.accepted()
                                                                          : LL.categoryRequests.denied()
                                                                }
                                                                size="small"
                                                                variant="outlined"
                                                                color={
                                                                    request.status === 'pending'
                                                                        ? 'warning'
                                                                        : request.status ===
                                                                            'accepted'
                                                                          ? 'success'
                                                                          : 'error'
                                                                }
                                                            />
                                                        </Box>
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {LL.categoryRequests.requestDate()}:{' '}
                                                        {formatDate(request.createdAt)}
                                                    </Typography>

                                                    {request.message && (
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {LL.categoryRequests.message()}:
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {request.message}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {request.adminNote && (
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                {LL.categoryRequests.adminNote()}:
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {request.adminNote}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {request.reviewedByName &&
                                                        request.reviewedAt && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    mt: 1,
                                                                    display: 'block',
                                                                }}
                                                            >
                                                                {LL.categoryRequests.reviewedBy({
                                                                    name: request.reviewedByName,
                                                                    date: formatDate(
                                                                        request.reviewedAt
                                                                    ),
                                                                })}
                                                            </Typography>
                                                        )}
                                                </Box>

                                                {request.status === 'pending' && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            flexDirection: {
                                                                xs: 'row',
                                                                sm: 'column',
                                                            },
                                                            width: {
                                                                xs: '100%',
                                                                sm: 'auto',
                                                            },
                                                        }}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            color="success"
                                                            startIcon={<AcceptIcon />}
                                                            onClick={() =>
                                                                handleOpenConfirm(request, 'accept')
                                                            }
                                                            disabled={isUpdating}
                                                            fullWidth
                                                            sx={{
                                                                display: {
                                                                    xs: 'flex',
                                                                    sm: 'none',
                                                                },
                                                            }}
                                                        >
                                                            {LL.categoryRequests.accept()}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            startIcon={<DenyIcon />}
                                                            onClick={() =>
                                                                handleOpenConfirm(request, 'deny')
                                                            }
                                                            disabled={isUpdating}
                                                            fullWidth
                                                            sx={{
                                                                display: {
                                                                    xs: 'flex',
                                                                    sm: 'none',
                                                                },
                                                            }}
                                                        >
                                                            {LL.categoryRequests.deny()}
                                                        </Button>
                                                        <Tooltip
                                                            title={LL.categoryRequests.accept()}
                                                        >
                                                            <IconButton
                                                                color="success"
                                                                onClick={() =>
                                                                    handleOpenConfirm(
                                                                        request,
                                                                        'accept'
                                                                    )
                                                                }
                                                                disabled={isUpdating}
                                                                sx={{
                                                                    display: {
                                                                        xs: 'none',
                                                                        sm: 'inline-flex',
                                                                    },
                                                                }}
                                                            >
                                                                <AcceptIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={LL.categoryRequests.deny()}>
                                                            <IconButton
                                                                color="error"
                                                                onClick={() =>
                                                                    handleOpenConfirm(
                                                                        request,
                                                                        'deny'
                                                                    )
                                                                }
                                                                disabled={isUpdating}
                                                                sx={{
                                                                    display: {
                                                                        xs: 'none',
                                                                        sm: 'inline-flex',
                                                                    },
                                                                }}
                                                            >
                                                                <DenyIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        )}
                    </Box>
                )}

                {/* Confirm accept/deny */}
                <Dialog open={confirmOpen} onClose={handleCloseConfirm} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {confirmAction === 'accept'
                            ? LL.categoryRequests.confirmAccept()
                            : LL.categoryRequests.confirmDeny()}
                    </DialogTitle>
                    <DialogContent>
                        {selectedRequest && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                    <strong>{LL.categoryRequests.requester()}:</strong>{' '}
                                    {selectedRequest.resourceName}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    <strong>{LL.categoryRequests.category()}:</strong>{' '}
                                    {selectedRequest.categoryName}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                    <strong>{LL.categoryRequests.requestType()}:</strong>{' '}
                                    {selectedRequest.requestType === 'join'
                                        ? LL.categoryRequests.join()
                                        : LL.categoryRequests.leave()}
                                </Typography>
                            </Box>
                        )}

                        <TextField
                            label={LL.categoryRequests.adminNoteLabel()}
                            multiline
                            rows={3}
                            value={adminNote}
                            onChange={(e) => setAdminNote(e.target.value)}
                            helperText={LL.categoryRequests.adminNoteHelp()}
                            fullWidth
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseConfirm} disabled={isUpdating}>
                            {LL.common.cancel()}
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            color={confirmAction === 'accept' ? 'success' : 'error'}
                            disabled={isUpdating}
                            startIcon={
                                isUpdating ? (
                                    <CircularProgress size={20} />
                                ) : confirmAction === 'accept' ? (
                                    <AcceptIcon />
                                ) : (
                                    <DenyIcon />
                                )
                            }
                        >
                            {confirmAction === 'accept'
                                ? LL.categoryRequests.accept()
                                : LL.categoryRequests.deny()}
                        </Button>
                    </DialogActions>
                </Dialog>
            </DialogContent>

            <DialogActions>
                <Button onClick={() => refetch()} disabled={!canReview || isLoading}>
                    {LL.common.refresh()}
                </Button>
                <Button onClick={onClose}>{LL.common.close()}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CategoryRequestsReviewModal;
